<template>
  <div>
    <b-card title="Опубликовать рейтинг">
      <b-form-group
          :label="`Презентующий текст (${ratingText.length} симв.)`"
          label-for="rating-text"
      >
        <b-form-textarea
            id="rating-text"
            v-model="ratingText"
            rows="4"
        />
      </b-form-group>

      <div class="d-flex align-items-center mb-1">
        <b-form-checkbox v-model="publishMusic" switch class="mr-2">
          🎵 Музыка
        </b-form-checkbox>
        <b-form-checkbox v-model="publishMovies" switch>
          🎬 Кино
        </b-form-checkbox>
      </div>

      <b-button
          variant="success"
          :disabled="!ratingText || isPublishingRating || (!publishMusic && !publishMovies)"
          @click="publishRating"
      >
        <span v-if="isPublishingRating">
          <b-spinner small />
          Публикация...
        </span>
        <span v-else>
          <feather-icon icon="ImageIcon" class="mr-50" />
          Опубликовать рейтинг
        </span>
      </b-button>
    </b-card>

    <b-card title="Игровые недели">
      <b-overlay :show="isLoading" rounded="sm">
        <b-form-group label="Игровая неделя" label-for="week-select" class="mb-0">
          <v-select
              id="week-select"
              v-model="selectedWeek"
              :options="weeks"
              label="name"
              :reduce="w => w"
              placeholder="Выберите неделю..."
              @input="onWeekChange"
          />
        </b-form-group>
      </b-overlay>
    </b-card>

    <b-card v-if="selectedWeek" title="Создание постов">
      <b-overlay :show="isLoadingGames" rounded="sm">
        <b-alert v-if="!isLoadingGames && days.length === 0" show variant="warning">
          В этой неделе нет игр
        </b-alert>

        <template v-if="days.length > 0">
          <div
              v-for="day in days"
              :key="day.date"
              class="border rounded p-1 mb-1"
          >
            <b-form-checkbox v-model="day.checked" class="font-weight-bold">
              {{ formatDayLabel(day.date) }}
            </b-form-checkbox>
            <div
                v-for="game in day.games"
                :key="game.id"
                class="ml-2 mt-50 text-muted"
            >
              {{ game.time }} — {{ game.topic.emoji }} {{ game.topic.name }} — {{ game.host }}
            </div>
          </div>

          <b-form-checkbox v-model="createFull" class="mt-1 mb-1">
            Общий анонс на несколько дней
          </b-form-checkbox>

          <b-button
              variant="warning"
              :disabled="checkedDays.length === 0 || isCreatingPosts"
              @click="createPosts"
          >
            <span v-if="isCreatingPosts">
              <b-spinner small />
              Создание...
            </span>
            <span v-else>
              <feather-icon icon="FileTextIcon" class="mr-50" />
              Создать посты
            </span>
          </b-button>
        </template>
      </b-overlay>
    </b-card>

    <b-card v-if="selectedWeek" title="Подвести итоги">
      <b-overlay :show="isLoadingSummary" rounded="sm">
        <b-button
            variant="primary"
            :disabled="isLoadingSummary"
            @click="loadSummary"
        >
          <feather-icon icon="AwardIcon" class="mr-50" />
          Сформировать итоги
        </b-button>

        <template v-if="summaryText !== null">
          <b-form-group
              :label="`Текст поста (${summaryText.length} симв.)`"
              label-for="summary-text"
              class="mt-2"
          >
            <b-form-textarea
                id="summary-text"
                v-model="summaryText"
                rows="20"
            />
          </b-form-group>

          <b-button
              variant="success"
              :disabled="!summaryText || isPublishing"
              @click="publishSummary"
          >
            <span v-if="isPublishing">
              <b-spinner small />
              Публикация...
            </span>
            <span v-else>
              <feather-icon icon="SendIcon" class="mr-50" />
              Опубликовать в канал
            </span>
          </b-button>
        </template>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BFormGroup,
  BFormTextarea,
  BFormCheckbox,
  BButton,
  BOverlay,
  BAlert,
  BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { onUnmounted, ref, computed, watch } from '@vue/composition-api'
import store from '@/store'
import gameStoreModule from '@/views/game/gameStoreModule'
import playersStoreModule from '@/views/players/playersStoreModule'
import { renderRatingBlob } from '@/views/players/exportRatingImage'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BFormGroup,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BOverlay,
    BAlert,
    BSpinner,
    vSelect,
  },
  setup(props, { root }) {
    const STORE_MODULE_NAME = 'app-games'
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, gameStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const PLAYERS_STORE_MODULE_NAME = 'app-players'
    if (!store.hasModule(PLAYERS_STORE_MODULE_NAME)) {
      store.registerModule(PLAYERS_STORE_MODULE_NAME, playersStoreModule)
    }
    onUnmounted(() => {
      if (store.hasModule(PLAYERS_STORE_MODULE_NAME)) store.unregisterModule(PLAYERS_STORE_MODULE_NAME)
    })

    // Строка меток собирается из включённых кластеров
    const buildRatingText = (music, movies) => {
      const labels = []
      if (music) labels.push('🎵 Музыка')
      if (movies) labels.push('🎬 Кино')

      return `🏆 Рейтинг игроков «Своя Игра ФНФ» за последние 90 дней\n\n${labels.join('   ')}`
    }

    const toast = useToast()

    const isLoading = ref(false)
    const isLoadingGames = ref(false)
    const isCreatingPosts = ref(false)
    const isLoadingSummary = ref(false)
    const isPublishing = ref(false)
    const isPublishingRating = ref(false)
    const publishMusic = ref(true)
    const publishMovies = ref(true)
    // Последний авто-сгенерированный текст — чтобы отличить ручную правку от дефолта
    const lastAutoRatingText = ref(buildRatingText(true, true))
    const ratingText = ref(lastAutoRatingText.value)

    // Пока текст не правили вручную (равен последнему авто-шаблону) — пересобираем при смене кластеров
    watch([publishMusic, publishMovies], ([music, movies]) => {
      const nextText = buildRatingText(music, movies)
      if (ratingText.value === lastAutoRatingText.value) {
        ratingText.value = nextText
      }
      lastAutoRatingText.value = nextText
    })
    const weeks = ref([])
    const selectedWeek = ref(null)
    const days = ref([])
    const createFull = ref(true)
    const summaryText = ref(null)

    const checkedDays = computed(() => days.value.filter(d => d.checked).map(d => d.date))

    const showToast = (title, variant) => {
      toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'danger' ? 'AlertTriangleIcon' : 'CheckCircleIcon',
          variant,
        },
      })
    }

    const errorMessage = (error, fallback) => (
      (error.response && error.response.data && error.response.data.message) || fallback
    )

    // Сегодняшняя дата в Москве в формате YYYY-MM-DD
    const moscowToday = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Moscow' }).format(new Date())

    // Дефолт: отмечены дни от сегодня; если сегодня воскресенье — сегодняшний день снят
    // (повторяет прежнюю серверную логику отбора игр для постов)
    const isDayCheckedByDefault = date => {
      const today = moscowToday()
      if (date < today) return false
      if (date === today && new Date(`${date}T00:00:00`).getDay() === 0) return false
      return true
    }

    const formatDayLabel = date => new Date(`${date}T00:00:00`)
        .toLocaleDateString('ru-RU', {
          weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
        })

    isLoading.value = true
    store.dispatch('app-games/fetchGameWeeks')
        .then(response => {
          weeks.value = response.data.data
        })
        .catch(() => showToast('Ошибка загрузки недель', 'danger'))
        .finally(() => {
          isLoading.value = false
        })

    const onWeekChange = () => {
      days.value = []
      summaryText.value = null
      if (!selectedWeek.value) return

      isLoadingGames.value = true
      store.dispatch('app-games/fetchWeekGames', { week_id: selectedWeek.value.id })
          .then(response => {
            days.value = response.data.data.map(day => ({
              ...day,
              checked: isDayCheckedByDefault(day.date),
            }))
          })
          .catch(error => showToast(errorMessage(error, 'Ошибка загрузки игр недели'), 'danger'))
          .finally(() => {
            isLoadingGames.value = false
          })
    }

    const createPosts = () => {
      isCreatingPosts.value = true
      store.dispatch('app-games/createWeekPosts', {
        week_id: selectedWeek.value.id,
        days: checkedDays.value,
        create_full: createFull.value,
      })
          .then(response => showToast(response.data.message || 'Посты успешно созданы', 'success'))
          .catch(error => showToast(errorMessage(error, 'Ошибка при создании постов'), 'danger'))
          .finally(() => {
            isCreatingPosts.value = false
          })
    }

    const loadSummary = () => {
      isLoadingSummary.value = true
      store.dispatch('app-games/fetchWeekSummary', { week_id: selectedWeek.value.id })
          .then(response => {
            summaryText.value = response.data.data.text
          })
          .catch(error => showToast(errorMessage(error, 'Ошибка формирования итогов'), 'danger'))
          .finally(() => {
            isLoadingSummary.value = false
          })
    }

    const publishSummary = () => {
      root.$swal({
        title: 'Опубликовать пост в канале?',
        text: 'Пост будет сразу опубликован в ТГ-канале',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Опубликовать',
        cancelButtonText: 'Отмена',
        customClass: {
          confirmButton: 'btn btn-success',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (!result.value) return

        isPublishing.value = true
        store.dispatch('app-games/publishWeekSummary', {
          week_id: selectedWeek.value.id,
          text: summaryText.value,
        })
            .then(() => showToast('Пост опубликован в канале', 'success'))
            .catch(error => showToast(errorMessage(error, 'Ошибка публикации поста'), 'danger'))
            .finally(() => {
              isPublishing.value = false
            })
      })
    }

    const publishRating = () => {
      const clusters = []
      if (publishMusic.value) clusters.push('music')
      if (publishMovies.value) clusters.push('movies')

      const dialogText = clusters.length === 2
        ? 'Альбом из 2 картинок (Музыка и Кино) будет опубликован в ТГ-канале'
        : `Будет опубликована 1 картинка (${publishMusic.value ? 'Музыка' : 'Кино'}) в ТГ-канале`

      root.$swal({
        title: 'Опубликовать рейтинг в канале?',
        text: dialogText,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Опубликовать',
        cancelButtonText: 'Отмена',
        customClass: {
          confirmButton: 'btn btn-success',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        if (!result.value) return

        isPublishingRating.value = true
        try {
          const responses = await Promise.all(
            clusters.map(cluster => store.dispatch('app-players/fetchRating', { cluster })),
          )

          const playersByCluster = {}
          clusters.forEach((cluster, index) => {
            playersByCluster[cluster] = (responses[index].data && responses[index].data.players) || []
          })

          if (clusters.some(cluster => !playersByCluster[cluster].length)) {
            showToast('Недостаточно данных рейтинга для публикации', 'danger')
            return
          }

          const blobs = await Promise.all(
            clusters.map(cluster => renderRatingBlob({ players: playersByCluster[cluster], cluster })),
          )

          const blobByCluster = {}
          clusters.forEach((cluster, index) => {
            blobByCluster[cluster] = blobs[index]
          })

          await store.dispatch('app-games/publishRating', {
            text: ratingText.value,
            musicBlob: blobByCluster.music || null,
            moviesBlob: blobByCluster.movies || null,
          })

          showToast('Рейтинг опубликован в канале', 'success')
        } catch (error) {
          showToast(errorMessage(error, 'Ошибка публикации рейтинга'), 'danger')
        } finally {
          isPublishingRating.value = false
        }
      })
    }

    return {
      isLoading,
      isLoadingGames,
      isCreatingPosts,
      isLoadingSummary,
      isPublishing,
      weeks,
      selectedWeek,
      days,
      createFull,
      summaryText,
      checkedDays,
      onWeekChange,
      createPosts,
      loadSummary,
      publishSummary,
      isPublishingRating,
      ratingText,
      publishMusic,
      publishMovies,
      publishRating,
      formatDayLabel,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
